import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";

import Button from "../../components/Button/Button";
import Input from "../../components/Input/Input";
import { Modal, useModal } from "../../components/Modal/Modal";
import { toast } from "../../components/Toast/ToastManager";
import Loading from "../../components/Loading";

import { apiService } from "../../services/api.service";

import type { ColDef } from "ag-grid-community";
import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { StatusRenderer } from "./StatusRenderer";
import { RefundActionsRenderer } from './RefundActionsRenderer';
import TabContent, { ITab, useTabContent } from "../../components/TabContent/TabContent";

import "./BalancesPayouts.scss";

import { IClub, IAccountStatus } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

interface IStripeBalance {
  available: Array<{
    amount: number;
    currency: string;
  }>;
  pending: Array<{
    amount: number;
    currency: string;
  }>;
  connect_reserved?: Array<{
    amount: number;
    currency: string;
  }>;
  instant_available?: Array<{
    amount: number;
    currency: string;
  }>;
}



interface IStripePayoutSchedule {
  interval: string;
  delay_days: number;
  weekly_anchor?: string;
  monthly_anchor?: number;
}

interface IStripeAccount {
  id: string;
  business_profile?: {
    name?: string;
  };
  settings?: {
    dashboard?: {
      display_name?: string;
    };
  };
  email?: string;
}

interface IPayoutRow {
  id: string;
  amount: string;
  status: string;
  initiated: string;
  arrivalDate: string;
}

interface IRefundRow {
  id: string;
  amount: string;
  status: string;
  created: string;
  chargeId: string;
  canCancel: boolean;
}

interface IProps {
  hideHeader?: boolean;
  clubId?: string;
}
const BalancesPayouts: React.FC<IProps> = ({ hideHeader = false, clubId }) => {
  const { isShown, toggle } = useModal();
  const [modalContent, setModalContent] = useState<string>("");
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(true);
  const [currentClub, setCurrentClub] = useState<IClub>();
  const [balance, setBalance] = useState<IStripeBalance>();
  // undefined = not fetched yet (show loading state), null = fetched but
  // Stripe couldn't be reached for this account right now
  const [accountStatus, setAccountStatus] = useState<IAccountStatus | null>();
  const [accountType, setAccountType] = useState<string>(Constants.account_type.custom);
  // Whether this club has ANY account record configured in our DB — independent
  // of whether we can currently reach Stripe for it. Used to decide whether to
  // offer "Use an Existing Stripe Account" (only relevant before any account
  // has been set up at all).
  const [accountConfigured, setAccountConfigured] = useState<boolean>(false);
  const [accountLoadError, setAccountLoadError] = useState<boolean>(false);
  const [isDisconnecting, setIsDisconnecting] = useState<boolean>(false);

  const [payoutSchedule, setPayoutSchedule] = useState<IStripePayoutSchedule>();
  const [account, setAccount] = useState<IStripeAccount>();
  const [isUpdatingSchedule, setIsUpdatingSchedule] = useState<boolean>(false);
  const [rowData, setRowData] = useState<IPayoutRow[]>([]);
  const [rawPayoutsData, setRawPayoutsData] = useState<any[]>([]);
  const [lifetimeVolume, setLifetimeVolume] = useState<number>(0);

  const [pendingFundsTimeline, setPendingFundsTimeline] = useState<Array<{
    date: string;
    amount: number;
    currency: string;
  }>>([]);
  
  // Modal state for payout schedule
  const [scheduleType, setScheduleType] = useState<'automatic' | 'manual'>('automatic');
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [weeklyDay, setWeeklyDay] = useState<string>('monday');
  const [monthlyDay, setMonthlyDay] = useState<number>(1);
  const [delayDays, setDelayDays] = useState<number>(7);

  // Manual payout state
  const [payoutError, setPayoutError] = useState<string | null>(null);
  const [lastPayoutRequest, setLastPayoutRequest] = useState<number | null>(null);
  const [isRequestingPayout, setIsRequestingPayout] = useState(false);
  const [payoutType, setPayoutType] = useState<'full' | 'custom'>('full');
  const [customPayoutAmount, setCustomPayoutAmount] = useState<string>('');

  // Tab state
  const { activeTab, setTab } = useTabContent();
  
  const tabs: ITab[] = [
    { name: "Payouts", actions: [] },
    { name: "Refunds", actions: [] },
    { name: "All Activity", actions: [] }
  ];

  // Refunds state
  const [refundsData, setRefundsData] = useState<IRefundRow[]>([]);
  const [refundsLoading, setRefundsLoading] = useState<boolean>(false);

  // All Activity state
  const [allActivityData, setAllActivityData] = useState<any[]>([]);
  const [allActivityLoading, setAllActivityLoading] = useState<boolean>(false);
  const [activityStartDate, setActivityStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split("T")[0];
  });
  const [activityEndDate, setActivityEndDate] = useState<string>(() => new Date().toISOString().split("T")[0]);

  // Payout Details Modal state
  const [selectedPayout, setSelectedPayout] = useState<any>(null);
  const [payoutTransactionsData, setPayoutTransactionsData] = useState<any[]>([]);
  const [loadingPayoutTransactions, setLoadingPayoutTransactions] = useState<boolean>(false);


  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto"
  });



  const weeklyOptions = [
    { label: "Monday", value: "monday" },
    { label: "Tuesday", value: "tuesday" },
    { label: "Wednesday", value: "wednesday" },
    { label: "Thursday", value: "thursday" },
    { label: "Friday", value: "friday" }
  ];

  const monthlyOptions = [
    { label: "1st", value: 1 },
    { label: "15th", value: 15 },
    { label: "Last day", value: 31 }
  ];

  const loadBalance = useCallback(async (clubId: string) => {
    try {
      const balanceData = await apiService.getStripeBalance(clubId);

      setBalance(balanceData);
    } catch (error) {
      console.error("Error loading balance:", error);
      // Set empty balance data to prevent errors
      setBalance({
        available: [{ amount: 0, currency: 'usd' }],
        pending: [{ amount: 0, currency: 'usd' }]
      });
    }
  }, []);

  const loadPayouts = useCallback(async (clubId: string) => {
    try {
      const payoutsData = await apiService.getStripePayouts(clubId);
      const payoutsArray = Array.isArray(payoutsData) ? payoutsData : [];
      
      
      
      // Store raw payouts data for calculations
      setRawPayoutsData(payoutsArray);
      
      // Transform data for AgGrid
      const rows: IPayoutRow[] = payoutsArray.map((payout) => ({
        id: payout.id,
        amount: formatCurrency(payout.amount, payout.currency),
        status: payout.status,
        initiated: formatShortDateTime(payout.created), // When it was initiated (format: "Jul 7, 10:57 PM")
        arrivalDate: formatShortDate(payout.arrival_date) // When it will arrive at bank (short format: "Jul 7")
      }));
      setRowData(rows);
    } catch (error) {
      console.error("Error loading payouts:", error);
      setRowData([]);
      setRawPayoutsData([]);
    }
  }, []);

  const loadPayoutSchedule = useCallback(async (clubId: string) => {
    try {
              const scheduleData = await apiService.getStripePayoutSchedule(clubId);
      setPayoutSchedule(scheduleData || undefined);
    } catch (error) {
      console.error("Error loading payout schedule:", error);
      setPayoutSchedule(undefined);
    }
  }, []);

  const loadAccount = useCallback(async (clubId: string) => {
    try {
      const accountData = await apiService.getStripeAccount(clubId);
      setAccountConfigured(!!accountData?._configured);
      setAccountLoadError(!!accountData?._stripe_error);
      // Only treat this as real Stripe account data (business name, email,
      // etc.) when the live Stripe call actually succeeded — a degraded
      // response only carries _account_type/_configured/_stripe_error.
      setAccount(accountData && !accountData._stripe_error ? accountData : undefined);
      // Always trust the DB-backed account_type, even when the live Stripe
      // call above failed. This must never silently fall back to the
      // "custom" default, or a club with a Standard account will incorrectly
      // see the Custom-account controls whenever Stripe is unreachable.
      if (accountData?._account_type) {
        setAccountType(accountData._account_type);
      }
      return accountData?._account_type as string | undefined;
    } catch (error) {
      console.error("Error loading account:", error);
      setAccount(undefined);
      setAccountLoadError(true);
      return undefined;
    }
  }, []);

  const loadAccountStatus = useCallback(async (clubId: string) => {
    try {
      // The shared apiService signature is typed as non-null for other
      // callers (event onboarding checks, etc.), but this specific endpoint
      // can legitimately return null when Stripe is unreachable for this
      // account — distinct from "not fetched yet" (undefined) here.
      const accountStatusData = (await apiService.getAccountStatus(clubId)) as IAccountStatus | null;
      setAccountStatus(accountStatusData ?? null);
    } catch (error) {
      console.error("Error loading account status:", error);
      setAccountStatus(null);
    }
  }, []);

  const loadRefunds = useCallback(async (clubId: string) => {
    try {
      setRefundsLoading(true);
      const refundsResponse = await apiService.getStripeRefunds(clubId, 100);
      
      const rows: IRefundRow[] = refundsResponse.refunds.map((refund) => ({
        id: refund.id,
        amount: formatCurrency(refund.amount, refund.currency),
        status: refund.status,
        created: formatShortDateTime(refund.created),
        chargeId: refund.charge || refund.payment_intent || '',
        canCancel: refund.status === 'pending' || refund.status === 'requires_action' || refund.status === 'succeeded'
      }));
      
      
      setRefundsData(rows);
    } catch (error) {
      console.error("Error loading refunds:", error);
      setRefundsData([]);
    } finally {
      setRefundsLoading(false);
    }
  }, []);


  const loadBalanceTransactions = useCallback(async (clubId: string) => {
    try {
      const transactions = await apiService.getStripeBalanceTransactions(clubId);
      
      // Process transactions to create pending funds timeline
      const pendingTransactions = transactions.filter(tx => 
        tx.status === 'pending' && 
        tx.available_on && 
        tx.net > 0 && // Only positive amounts (incoming funds)
        ['charge', 'payment', 'payment_intent'].includes(tx.type)
      );
      
      // Group by available_on date and sum amounts
      const timelineMap = new Map<string, { amount: number; currency: string }>();
      
      pendingTransactions.forEach(tx => {
        const availableDate = new Date(tx.available_on * 1000);
        const dateKey = availableDate.toISOString().split('T')[0]; // YYYY-MM-DD format
        
        const existing = timelineMap.get(dateKey) || { amount: 0, currency: tx.currency };
        existing.amount += tx.net; // tx.net is the amount after fees
        timelineMap.set(dateKey, existing);
      });
      
      // Convert to array and sort by date
      const timeline = Array.from(timelineMap.entries())
        .map(([date, { amount, currency }]) => ({
          date,
          amount,
          currency
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
      
      setPendingFundsTimeline(timeline);
    } catch (error) {
      console.error("Error loading balance transactions:", error);
      setPendingFundsTimeline([]);
    }
  }, []);

  const loadAllActivity = useCallback(async (clubId: string, startDate?: string, endDate?: string) => {
    try {
      setAllActivityLoading(true);
      const transactions = await apiService.getStripeBalanceTransactions(clubId, startDate, endDate);
      const rows = transactions.map((tx: any) => ({
        id: tx.id,
        created: tx.created,
        type: tx.type,
        description: tx.description || '',
        amount: tx.amount,
        fee: tx.fee,
        net: tx.net,
        currency: tx.currency,
        status: tx.status,
      }));
      setAllActivityData(rows);
    } catch (error) {
      console.error("Error loading all activity:", error);
      setAllActivityData([]);
    } finally {
      setAllActivityLoading(false);
    }
  }, []);

  const formatCurrency = (amount: number, currency: string = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
    }).format(amount / 100); // Stripe amounts are in cents
  };

  // const formatDate = (timestamp: number) => {
  //   return new Date(timestamp * 1000).toLocaleDateString('en-US', {
  //     year: 'numeric',
  //     month: '2-digit',
  //     day: '2-digit'
  //   });
  // };

  // const formatDateTime = (timestamp: number) => {
  //   return new Date(timestamp * 1000).toLocaleString('en-US', {
  //     year: 'numeric',
  //     month: '2-digit',
  //     day: '2-digit',
  //     hour: '2-digit',
  //     minute: '2-digit',
  //     hour12: true
  //   });
  // };

  const formatShortDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC'
    });
  };

  const formatShortDateTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };




  const updateBankAccount = () => {
    setModalContent("updateBank");
    toggle();
  };

  const handleConnectStandardAccount = async () => {
    if (!currentClub) return;
    try {
      const redirectUri = `${window.location.origin}/stripe/connect/return`;
      const { url } = await apiService.getStripeConnectUrl(currentClub._id, redirectUri);
      window.location.href = url;
    } catch (error) {
      console.error("Error getting Stripe connect URL:", error);
      toast.show({
        title: "Connection Failed",
        content: "Failed to initiate Stripe connection. Please try again.",
        duration: 5000,
        type: "fail",
      });
    }
  };

  const handleDisconnectStandardAccount = async () => {
    if (!currentClub) return;
    setIsDisconnecting(true);
    try {
      await apiService.disconnectStripeAccount(currentClub._id);
      toast.show({
        title: "Stripe Account Disconnected",
        content: "Your Standard Stripe account has been disconnected.",
        duration: 5000,
        type: "success",
      });
      toggle();
      // Reload the page to reflect the new state
      window.location.reload();
    } catch (error: any) {
      console.error("Error disconnecting Stripe account:", error);
      toast.show({
        title: "Disconnect Failed",
        content: error?.response?.data?.message || "Failed to disconnect account. Please try again.",
        duration: 5000,
        type: "fail",
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleAccountAction = async () => {
    if (!currentClub || !accountStatus) return;
    
    // If account needs onboarding information, redirect to Stripe onboarding
    if (accountStatus.onboarding_status === "past_due" || accountStatus.onboarding_status === "currently_due") {
      try {
        const returnUrl = `${window.location.origin}/balances-payouts`;
        const refreshUrl = `${window.location.origin}/balances-payouts`;
        
        const { url } = await apiService.getAccountOnboardingLink(currentClub._id, {
          return_url: returnUrl,
          refresh_url: refreshUrl
        });
        
        window.open(url, '_blank');
      } catch (error) {
        console.error("Error creating account onboarding link:", error);
        toast.show({
          title: "Update Failed",
          content: "Failed to create onboarding link. Please try again.",
          duration: 5000,
          type: "fail"
        });
      }
    } else {
      // For other cases, show the modal
      updateBankAccount();
    }
  };

  const updatePayoutSchedule = () => {
    if (!payoutSchedule) return;
    
    // Set form state from current schedule
    if (payoutSchedule.interval === 'manual') {
      setScheduleType('manual');
    } else {
      setScheduleType('automatic');
      setFrequency(payoutSchedule.interval as 'daily' | 'weekly' | 'monthly');
      setDelayDays(payoutSchedule.delay_days || 7);
      
      if (payoutSchedule.interval === 'weekly' && payoutSchedule.weekly_anchor) {
        setWeeklyDay(payoutSchedule.weekly_anchor);
      }
      
      if (payoutSchedule.interval === 'monthly' && payoutSchedule.monthly_anchor) {
        setMonthlyDay(payoutSchedule.monthly_anchor);
      }
    }
    
    setModalContent("updateSchedule");
    toggle();
  };

  const openModal = (content: string) => {
    setModalContent(content);
    if (content === "requestPayout") {
      setPayoutError(null); // Clear any previous errors
      setPayoutType('full'); // Reset to full amount
      setCustomPayoutAmount(''); // Clear custom amount
    }
    toggle();
  };

  const handleScheduleSubmit = async () => {

    
    if (!currentClub) {
      console.error("No current club found");
      toast.show({
        title: "Error",
        content: "No club selected. Please refresh the page and try again.",
        duration: 5000,
        type: "fail"
      });
      return;
    }
    
    setIsUpdatingSchedule(true);
    try {
      let scheduleData: any = {};

      if (scheduleType === 'manual') {
        scheduleData.interval = 'manual';
        // Don't include delay_days for manual payouts
      } else {
        scheduleData.interval = frequency;
        scheduleData.delay_days = delayDays; // Only include delay_days for automatic payouts
        
        if (frequency === 'weekly') {
          scheduleData.weekly_anchor = weeklyDay;
        } else if (frequency === 'monthly') {
          scheduleData.monthly_anchor = monthlyDay;
        }
      }

      await apiService.updateStripePayoutSchedule(currentClub._id, scheduleData);
      
      await loadPayoutSchedule(currentClub._id);
      
      toast.show({
        title: "Payout Schedule Updated",
        content: "Your payout schedule has been successfully updated.",
        duration: 5000,
        type: "success"
      });
      
      toggle();
    } catch (error: any) {
      console.error("Error updating payout schedule:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to update payout schedule. Please try again.";
      toast.show({
        title: "Update Failed",
        content: errorMessage,
        duration: 8000,
        type: "fail"
      });
    } finally {
      setIsUpdatingSchedule(false);
    }
  };

  const handleUpdateBankAccount = async () => {
    if (!currentClub) {
      console.error("No current club available");
      return;
    }

    try {
      // Create Stripe Connect onboarding link
      const response = await apiService.getAccountOnboardingLink(currentClub._id, {
        return_url: `${window.location.origin}/balances-payouts`,
        refresh_url: `${window.location.origin}/balances-payouts`
      });

      if (response.url) {
        // Redirect to Stripe Connect onboarding
        window.open(response.url, '_blank');
      } else {
        console.error("No onboarding URL received from Stripe");
      }
    } catch (error) {
      console.error("Error creating Stripe onboarding link:", error);
    } finally {
      toggle(); // Close the modal
    }
  };

  // Manual payout request handler
  const handleRequestPayout = async () => {
    if (!currentClub) return;

    // Check if request was made recently (within last 5 minutes)
    const now = Date.now();
    if (lastPayoutRequest !== null && (now - lastPayoutRequest) < 5 * 60 * 1000) {
      setPayoutError("Please wait at least 5 minutes between payout requests.");
      return;
    }

    // Check if there are available funds
    const { available, currency } = getBalanceAmounts();
    if (available <= 0) {
      setPayoutError("No available funds to payout. Available balance must be greater than $0.");
      return;
    }

    // Determine payout amount based on type
    let payoutAmount = available;
    if (payoutType === 'custom') {
      const customAmountCents = Math.round(parseFloat(customPayoutAmount) * 100);
      
      // Validate custom amount
      if (isNaN(customAmountCents) || customAmountCents <= 0) {
        setPayoutError("Please enter a valid payout amount greater than $0.");
        return;
      }
      
      if (customAmountCents > available) {
        setPayoutError(`Payout amount cannot exceed available balance of ${formatCurrency(available, currency)}.`);
        return;
      }
      
      payoutAmount = customAmountCents;
    }

    setIsRequestingPayout(true);
    setPayoutError(null);

    try {
      // Request manual payout for specified amount
      await apiService.requestManualPayout(currentClub._id, {
        amount: payoutAmount,
        currency: currency,
        description: `Manual payout requested on ${new Date().toISOString()}`
      });

      // Update last request time
      setLastPayoutRequest(now);

      // Reload data to reflect changes
      await Promise.all([
        loadBalance(currentClub._id),
        loadPayouts(currentClub._id)
      ]);

      toast.show({
        title: "Payout Requested",
        content: "Your payout request has been submitted successfully! It will appear in your payouts list shortly.",
        duration: 5000,
        type: "success"
      });
      
      // Reset form state
      setPayoutType('full');
      setCustomPayoutAmount('');
      toggle(); // Close the modal after successful request
    } catch (error: any) {
      console.error("Error requesting payout:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to request payout. Please try again.";
      setPayoutError(errorMessage);
    } finally {
      setIsRequestingPayout(false);
    }
  };

  // Handler for payout row click
  const handlePayoutRowClick = useCallback(async (event: any) => {
    const payoutId = event.data.id;
    const payoutData = rawPayoutsData.find(p => p.id === payoutId);
    
    if (!payoutData || !currentClub) return;
    
    setSelectedPayout(payoutData);
    setModalContent("payoutDetails");
    toggle();
    
    // Load transactions for this payout
    await loadPayoutTransactions(payoutId);
  }, [rawPayoutsData, currentClub]);

  // Load and enrich payout transactions
  const loadPayoutTransactions = async (payoutId: string) => {
    if (!currentClub) return;
    
    setLoadingPayoutTransactions(true);
    try {
      // Get Stripe balance transactions for this payout
      const stripeTransactions = await apiService.getPayoutTransactions(currentClub._id, payoutId);
      
      // Extract charge IDs from transactions
      // In Stripe balance transactions, source can be either a string or an object with an id
      // ACH / bank debit charges have py_ IDs instead of ch_
      const isChargeId = (id?: string) => !!id && (id.startsWith('ch_') || id.startsWith('py_'));
      const chargeIds = stripeTransactions
        .filter(tx => {
          const sourceId = typeof tx.source === 'string' ? tx.source : tx.source?.id;
          return isChargeId(sourceId);
        })
        .map(tx => typeof tx.source === 'string' ? tx.source : tx.source.id);
      
      // Get our transaction records by charge IDs
      let ourTransactions: any[] = [];
      if (chargeIds.length > 0) {
        ourTransactions = await apiService.getTransactionsByChargeIds(currentClub._id, chargeIds);
      }
      
      // Create a map of charge_id -> transaction data
      const transactionMap = new Map();
      ourTransactions.forEach(tx => {
        if (tx.references?.charge_id) {
          transactionMap.set(tx.references.charge_id, tx);
        }
      });
      
      // Enrich Stripe transactions with our data
      const enrichedTransactions = stripeTransactions.map(stripeTx => {
        const sourceId = typeof stripeTx.source === 'string' ? stripeTx.source : stripeTx.source?.id;
        const chargeId = isChargeId(sourceId) ? sourceId : null;
        const ourTx = chargeId ? transactionMap.get(chargeId) : null;
        const platformTransfer = !ourTx ? stripeTx.source_transfer : null;

        if (platformTransfer) {
          return {
            id: stripeTx.id,
            created: stripeTx.created,
            type: 'transfer',
            amount: stripeTx.amount,
            fee: stripeTx.fee,
            net: stripeTx.net,
            currency: stripeTx.currency,
            description: platformTransfer.description,
            source: stripeTx.source,
            eventName: platformTransfer.description || 'Transfer from Eager Dog',
            ownerName: 'Eager Dog',
            ownerEmail: 'N/A',
            paymentMethod: 'Transfer',
            transactionId: 'N/A',
            hasInternalRecord: false
          };
        }
        
        // Get payment method from our transaction record, or show N/A for non-charge transactions
        let paymentMethod = 'N/A';
        if (ourTx?.amounts?.payment_method_type) {
          // Capitalize first letter of payment method
          paymentMethod = ourTx.amounts.payment_method_type.charAt(0).toUpperCase() + 
                         ourTx.amounts.payment_method_type.slice(1);
        } else if (stripeTx.type === 'payout') {
          paymentMethod = 'Payout';
        } else if (stripeTx.type === 'stripe_fee' || stripeTx.type === 'application_fee') {
          paymentMethod = 'Fee';
        }
        
        return {
          id: stripeTx.id, // Stripe balance transaction ID (for AGGrid row ID)
          created: stripeTx.created,
          type: stripeTx.type,
          amount: stripeTx.amount,
          fee: stripeTx.fee,
          net: stripeTx.net,
          currency: stripeTx.currency,
          description: stripeTx.description,
          source: stripeTx.source,
          // Enriched data from our denormalized transaction records
          // Fallback to populated refs for older transactions
          eventName: ourTx?.event_name || ourTx?.event_id?.title || 'N/A',
          ownerName: (ourTx?.user_first_name && ourTx?.user_last_name)
            ? `${ourTx.user_first_name} ${ourTx.user_last_name}`
            : (ourTx?.user_id?.first_name && ourTx?.user_id?.last_name)
              ? `${ourTx.user_id.first_name} ${ourTx.user_id.last_name}`
              : 'N/A',
          ownerEmail: ourTx?.user_email || ourTx?.user_id?.email || 'N/A',
          paymentMethod,
          transactionId: ourTx?._id || 'N/A', // Our internal transaction ID
          hasInternalRecord: !!ourTx
        };
      });
      
      setPayoutTransactionsData(enrichedTransactions);
    } catch (error) {
      console.error("Error loading payout transactions:", error);
      toast.show({
        title: "Error",
        content: "Failed to load payout transactions",
        duration: 5000,
        type: "fail"
      });
      setPayoutTransactionsData([]);
    } finally {
      setLoadingPayoutTransactions(false);
    }
  };

  // Format transaction type for display
  const formatTransactionType = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      'charge': 'Charge',
      'payment': 'Charge',
      'payment_refund': 'Refund',
      'refund': 'Refund',
      'payout': 'Payout',
      'stripe_fee': 'Stripe Fee',
      'adjustment': 'Adjustment',
      'application_fee': 'Application Fee',
      'application_fee_refund': 'Fee Refund',
      'transfer': 'Transfer',
      'transfer_refund': 'Transfer Refund'
    };
    return typeMap[type] || type;
  };


  const getModalContent = () => {
    switch (modalContent) {
      case "updateBank":
        return (
          <div className="update-bank-modal">
            <h3>Update Account Information</h3>
            <p>You'll be redirected to Stripe to update your Stripe account information.</p>
            <div className="modal-actions">
              <Button onClick={toggle} className="secondary">Cancel</Button>
              <Button onClick={handleUpdateBankAccount}>Continue to Stripe</Button>
            </div>
          </div>
        );

      case "connectStandard":
        return (
          <div className="update-bank-modal">
            <h3>Connect Existing Stripe Account</h3>
            <p>Connect your own Standard Stripe account to receive payouts directly. You'll be redirected to Stripe to authorize the connection.</p>
            <p style={{ fontSize: "13px", color: "#888", marginTop: "8px" }}>
              Your payout schedule and bank account will be managed directly in your Stripe Dashboard.
            </p>
            <div className="modal-actions">
              <Button onClick={toggle} className="secondary">Cancel</Button>
              <Button onClick={handleConnectStandardAccount}>Connect to Stripe</Button>
            </div>
          </div>
        );

      case "disconnectStandard":
        return (
          <div className="update-bank-modal">
            <h3>Disconnect Stripe Account</h3>
            <p>Are you sure you want to disconnect your Standard Stripe account? You will no longer be able to receive payouts until you reconnect an account.</p>
            <div className="modal-actions">
              <Button onClick={toggle} className="secondary">Cancel</Button>
              <Button onClick={handleDisconnectStandardAccount} disabled={isDisconnecting}>
                {isDisconnecting ? "Disconnecting..." : "Disconnect"}
              </Button>
            </div>
          </div>
        );
      
      case "updateSchedule":
        return (
          <div className="payout-schedule-modal">
            <h3>Edit Payout Schedule</h3>
            
            {/* Automatic Option */}
            <div className="schedule-option">
              <label className="radio-option">
                <input
                  type="radio"
                  name="scheduleType"
                  checked={scheduleType === 'automatic'}
                  onChange={() => setScheduleType('automatic')}
                  disabled={isUpdatingSchedule}
                />
                <span className="radio-label">Automatic — Every</span>
                
                {scheduleType === 'automatic' && (
                  <select 
                    value={frequency} 
                    onChange={(e) => setFrequency(e.target.value as 'daily' | 'weekly' | 'monthly')}
                    disabled={isUpdatingSchedule}
                    className="frequency-select"
                  >
                    <option value="daily">day</option>
                    <option value="weekly">week</option>
                    <option value="monthly">month</option>
                  </select>
                )}
              </label>
              
              {scheduleType === 'automatic' && frequency === 'weekly' && (
                <div className="sub-option">
                  <label>Day of week:</label>
                  <select 
                    value={weeklyDay} 
                    onChange={(e) => setWeeklyDay(e.target.value)}
                    disabled={isUpdatingSchedule}
                  >
                    {weeklyOptions.map(day => (
                      <option key={day.value} value={day.value}>{day.label}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {scheduleType === 'automatic' && frequency === 'monthly' && (
                <div className="sub-option">
                  <label>Day of month:</label>
                  <select 
                    value={monthlyDay} 
                    onChange={(e) => setMonthlyDay(Number(e.target.value))}
                    disabled={isUpdatingSchedule}
                  >
                    {monthlyOptions.map(day => (
                      <option key={day.value} value={day.value}>{day.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Delay Payouts Option */}
            {scheduleType === 'automatic' && (
              <div className="delay-option">
                <label>Delay payouts by</label>
                <select 
                  value={delayDays} 
                  onChange={(e) => setDelayDays(Number(e.target.value))}
                  disabled={isUpdatingSchedule}
                  className="delay-select"
                >
                  {Array.from({length: 25}, (_, i) => i + 7).map(days => (
                    <option key={days} value={days}>{days} days</option>
                  ))}
                </select>
              </div>
            )}

            {/* Manual Option */}
            <div className="schedule-option">
              <label className="radio-option">
                <input
                  type="radio"
                  name="scheduleType"
                  checked={scheduleType === 'manual'}
                  onChange={() => setScheduleType('manual')}
                  disabled={isUpdatingSchedule}
                />
                <span className="radio-label">Manual - you choose when to receive your payouts</span>
              </label>
            </div>

            <div className="modal-actions">
              <Button onClick={toggle} className="secondary">Cancel</Button>
              <Button onClick={handleScheduleSubmit} disabled={isUpdatingSchedule}>
                {isUpdatingSchedule ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        );
      
      case "requestPayout":
        const { available: modalAvailable, currency: modalCurrency } = getBalanceAmounts();
        const hasAvailableFunds = modalAvailable > 0;
        const hasRecentRequest = lastPayoutRequest !== null && (Date.now() - lastPayoutRequest) < 5 * 60 * 1000;
        
        // Validate custom amount for button state
        const isValidCustomAmount = payoutType === 'full' || 
          (customPayoutAmount !== '' && 
           !isNaN(parseFloat(customPayoutAmount)) && 
           parseFloat(customPayoutAmount) > 0 && 
           Math.round(parseFloat(customPayoutAmount) * 100) <= modalAvailable);
        
        return (
          <div className="request-payout-modal">
            <h3>Request Payout</h3>
            <p>Choose your payout amount:</p>
            
            {/* Payout Type Selection */}
            <div className="payout-type-selection" style={{ marginBottom: '20px' }}>
              <div className="radio-option" style={{ marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="payoutType"
                    value="full"
                    checked={payoutType === 'full'}
                    onChange={(e) => {
                      setPayoutType('full');
                      setPayoutError(null);
                    }}
                    style={{ marginRight: '10px' }}
                  />
                  <div>
                    <div style={{ fontWeight: 'bold' }}>Full Available Amount</div>
                    <div style={{ color: '#666', fontSize: '14px' }}>
                      {formatCurrency(modalAvailable, modalCurrency)} • Free • Arrives in 2-7 business days
                    </div>
                  </div>
                </label>
              </div>
              
              <div className="radio-option">
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="payoutType"
                    value="custom"
                    checked={payoutType === 'custom'}
                    onChange={(e) => {
                      setPayoutType('custom');
                      setPayoutError(null);
                    }}
                    style={{ marginRight: '10px' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold' }}>Custom Amount</div>
                    <div style={{ color: '#666', fontSize: '14px' }}>
                      Free • Arrives in 2-7 business days
                    </div>
                  </div>
                </label>
                
                {payoutType === 'custom' && (
                  <div style={{ marginTop: '10px', marginLeft: '30px' }}>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={customPayoutAmount}
                      onChange={(e) => {
                        setCustomPayoutAmount(e.target.value);
                        setPayoutError(null);
                      }}
                      min="0.01"
                      max={(modalAvailable / 100).toFixed(2)}
                      step="0.01"
                      style={{
                        width: '150px',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '16px'
                      }}
                    />
                    <span style={{ marginLeft: '8px', color: '#666' }}>
                      USD (Max: {formatCurrency(modalAvailable, modalCurrency)})
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Error Messages */}
            {!hasAvailableFunds && (
              <div className="error-message" style={{ color: 'red', marginBottom: '10px' }}>
                No available funds for payout. Available balance is {formatCurrency(modalAvailable, modalCurrency)}.
              </div>
            )}
            
            {hasRecentRequest && (
              <div className="error-message" style={{ color: 'red', marginBottom: '10px' }}>
                Please wait at least 5 minutes between payout requests.
              </div>
            )}
            
            {payoutError && (
              <div className="error-message" style={{ color: 'red', marginBottom: '10px' }}>
                {payoutError}
              </div>
            )}
            
            <div className="modal-actions">
              <Button onClick={toggle} className="secondary">Cancel</Button>
              <Button 
                onClick={handleRequestPayout}
                disabled={isRequestingPayout || hasRecentRequest || !hasAvailableFunds || !isValidCustomAmount}
              >
                {isRequestingPayout ? "Requesting..." : "Request Payout"}
              </Button>
            </div>
          </div>
        );
      
      case "viewSchedule":
        return (
          <div className="view-schedule-modal">
            <h3>Payout Schedule Details</h3>
            
            <div className="schedule-info">
              <div className="schedule-current">
                <strong>Current Schedule:</strong>
                <span>{getCurrentScheduleText()}</span>
              </div>
              
              {pendingFundsTimeline.length > 0 && (
                <div className="pending-funds-section">
                  <strong>Upcoming Fund Availability:</strong>
                  <div className="availability-subtext">Estimated dates from Stripe</div>
                  <div className="funds-timeline">
                    {pendingFundsTimeline.map((item, index) => (
                      <div key={item.date} className="timeline-entry">
                        <span className="timeline-amount">
                          {formatCurrency(item.amount, item.currency)}
                        </span>
                        <span className="timeline-date">
                          {(() => {
                            const date = new Date(item.date);
                            const today = new Date();
                            const tomorrow = new Date(today);
                            tomorrow.setDate(today.getDate() + 1);

                            if (date.toDateString() === today.toDateString()) {
                              return 'Today';
                            } else if (date.toDateString() === tomorrow.toDateString()) {
                              return 'Tomorrow';
                            } else {
                              return date.toLocaleDateString('en-US', { 
                                month: 'short', 
                                day: 'numeric',
                                year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
                              });
                            }
                          })()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {pendingFundsTimeline.length === 0 && (
                <div className="no-pending-funds">
                  <em>No pending funds scheduled to become available</em>
                </div>
              )}
            </div>
            
            <div className="modal-actions">
              <Button onClick={toggle}>Close</Button>
            </div>
          </div>
        );


      case "payoutDetails":
        // Column definitions for the payout transactions table
        const transactionColDefs: ColDef[] = [
          {
            headerName: "Transaction Date",
            field: "created",
            sortable: true,
            filter: true,
            sort: 'desc',
            valueFormatter: (params) => {
              if (!params.value) return '';
              return formatShortDateTime(params.value);
            },
            width: 190
          },
          {
            headerName: "Type",
            field: "type",
            sortable: true,
            filter: true,
            valueFormatter: (params) => formatTransactionType(params.value),
            width: 130
          },
          {
            headerName: "Event Name",
            field: "eventName",
            sortable: true,
            filter: true,
            width: 200,
            cellStyle: (params) => {
              if (params.value === 'N/A') {
                return { color: '#999', fontStyle: 'italic' };
              }
              return null;
            }
          },
          {
            headerName: "Owner Name",
            field: "ownerName",
            sortable: true,
            filter: true,
            width: 180,
            cellStyle: (params) => {
              if (params.value === 'N/A') {
                return { color: '#999', fontStyle: 'italic' };
              }
              return null;
            }
          },
          {
            headerName: "Owner Email",
            field: "ownerEmail",
            sortable: true,
            filter: true,
            width: 220,
            cellStyle: (params) => {
              if (params.value === 'N/A') {
                return { color: '#999', fontStyle: 'italic' };
              }
              return null;
            }
          },
          {
            headerName: "Payment Method",
            field: "paymentMethod",
            sortable: true,
            filter: true,
            width: 165
          },
          {
            headerName: "Gross Amount",
            field: "amount",
            sortable: true,
            filter: true,
            valueFormatter: (params) => {
              if (params.value === undefined || params.value === null) return '';
              const amount = params.value;
              const currency = params.data?.currency || 'usd';
              return formatCurrency(amount, currency);
            },
            width: 160,
            type: 'rightAligned'
          },
          {
            headerName: "Fee Amount",
            field: "fee",
            sortable: true,
            filter: true,
            valueFormatter: (params) => {
              if (params.value === undefined || params.value === null) return '';
              const fee = params.value;
              const currency = params.data?.currency || 'usd';
              if (fee < 0) {
                return `(${formatCurrency(Math.abs(fee), currency)})`;
              }
              return formatCurrency(fee, currency);
            },
            width: 140,
            type: 'rightAligned'
          },
          {
            headerName: "Net Amount",
            field: "net",
            sortable: true,
            filter: true,
            valueFormatter: (params) => {
              if (params.value === undefined || params.value === null) return '';
              const net = params.value;
              const currency = params.data?.currency || 'usd';
              if (net < 0) {
                return `(${formatCurrency(Math.abs(net), currency)})`;
              }
              return formatCurrency(net, currency);
            },
            width: 140,
            type: 'rightAligned'
          },
          {
            headerName: "Transaction ID",
            field: "transactionId",
            sortable: true,
            filter: true,
            width: 200,
            cellStyle: (params) => {
              // Gray out N/A values
              if (params.value === 'N/A') {
                return { color: '#999', fontStyle: 'italic' };
              }
              return null;
            }
          }
        ];

        const defaultTransactionColDef: ColDef = {
          resizable: true,
          suppressMovable: true,
          minWidth: 100
        };

        return (
          <div className="payout-details-modal">
            <h3>Payout Details</h3>
            <div className="payout-summary">
              <div className="summary-item">
                <strong>Amount:</strong> {selectedPayout ? formatCurrency(selectedPayout.amount, selectedPayout.currency) : 'N/A'}
              </div>
              <div className="summary-item">
                <strong>Status:</strong> {selectedPayout?.status ? selectedPayout.status.charAt(0).toUpperCase() + selectedPayout.status.slice(1) : 'N/A'}
              </div>
              <div className="summary-item">
                <strong>Initiated:</strong> {selectedPayout ? formatShortDateTime(selectedPayout.created) : 'N/A'}
              </div>
              <div className="summary-item">
                <strong>Arrival Date:</strong> {selectedPayout ? formatShortDate(selectedPayout.arrival_date) : 'N/A'}
              </div>
            </div>
            
            <div className="transactions-table-wrap">
              {loadingPayoutTransactions ? (
                <div className="skeleton-loader">
                  <div className="skeleton-header"></div>
                  <div className="skeleton-row"></div>
                  <div className="skeleton-row"></div>
                  <div className="skeleton-row"></div>
                  <div className="skeleton-row"></div>
                  <div className="skeleton-row"></div>
                </div>
              ) : payoutTransactionsData.length === 0 ? (
                <div className="no-data-message">
                  <p>No transactions found for this payout.</p>
                </div>
              ) : (
                <div style={{ height: '400px', width: '100%' }}>
                  <AgGridReact
                    rowData={payoutTransactionsData}
                    suppressRowTransform={true}
                    columnDefs={transactionColDefs}
                    defaultColDef={defaultTransactionColDef}
                    theme={theme}
                    pagination={true}
                    paginationPageSize={20}
                    suppressCellFocus={true}
                    getRowId={(params) => params.data.id}
                    rowHeight={50}
                    headerHeight={50}
                  />
                </div>
              )}
            </div>
            
            <div className="modal-actions">
              <Button onClick={toggle}>Close</Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getCurrentScheduleText = () => {
    if (!payoutSchedule || !payoutSchedule.interval) return "Not set";
    
    if (payoutSchedule?.interval === 'manual') {
      return `Manual — ${payoutSchedule.delay_days || 7} day rolling basis`;
    }

    const intervalText = payoutSchedule.interval.charAt(0).toUpperCase() + payoutSchedule.interval.slice(1);
    return `${intervalText} - ${payoutSchedule.delay_days} day rolling basis`;
  };

  const getAccountName = () => {
    let displayName = "";
    
    if (account?.business_profile?.name) {
      displayName = account.business_profile.name;
    } else if (account?.settings?.dashboard?.display_name) {
      displayName = account.settings.dashboard.display_name;
    } else if (account?.email) {
      displayName = account.email;
    } else {
      displayName = "Not configured";
    }
    
    // Add account ID if available
    if (account?.id && displayName !== "Not configured") {
      return `${displayName} (${account.id})`;
    }
    
    return displayName;
  };

  const getAccountStatusMessage = () => {
    if (accountStatus === undefined) {
      return { message: "Loading...", status: "loading", actionNeeded: false };
    }

    if (accountStatus === null) {
      // Fetched successfully, but we couldn't reach Stripe for this account
      // right now (e.g. a Standard account that's fine to check directly in
      // its own Stripe Dashboard, or a transient connection issue).
      return accountType === Constants.account_type.standard
        ? {
            message: "Account status is managed directly in your connected Stripe account.",
            status: "info",
            actionNeeded: false,
          }
        : {
            message: "Unable to verify account status with Stripe right now. Please try again shortly.",
            status: "unknown",
            actionNeeded: false,
          };
    }

    switch (accountStatus.onboarding_status) {
      case "past_due":
        return {
          message: "Action required: Important information is missing and must be provided immediately to continue processing payments.",
          status: "past_due",
          actionNeeded: true
        };
      case "currently_due":
        return {
          message: "Action required: Additional information is needed to maintain your account in good standing.",
          status: "currently_due", 
          actionNeeded: true
        };
      case "eventually_due":
        return {
          message: "Information will eventually be required, but your account is active and can process payments.",
          status: "eventually_due",
          actionNeeded: false
        };
      case "requirements_met":
        return {
          message: "Your Stripe account is complete. You are all set to take payouts.",
          status: "requirements_met",
          actionNeeded: false
        };
      default:
        return { message: "Unknown status", status: "unknown", actionNeeded: false };
    }
  };

  const getBalanceAmounts = useCallback(() => {
    if (!balance) return { available: 0, pending: 0, inTransitToBank: 0, instantAvailable: 0, currency: 'usd' };
    

    
    // Aggregate amounts by currency
    const currencyTotals: { [currency: string]: { available: number; pending: number; inTransitToBank: number; instantAvailable: number } } = {};
    
    // Process available balances
    if (balance.available && Array.isArray(balance.available)) {
      balance.available.forEach(item => {
        const currency = item.currency?.toLowerCase() || 'usd';
        if (!currencyTotals[currency]) {
          currencyTotals[currency] = { available: 0, pending: 0, inTransitToBank: 0, instantAvailable: 0 };
        }
        currencyTotals[currency].available += item.amount || 0;
      });
    }
    
    // Process pending balances  
    if (balance.pending && Array.isArray(balance.pending)) {
      balance.pending.forEach(item => {
        const currency = item.currency?.toLowerCase() || 'usd';
        if (!currencyTotals[currency]) {
          currencyTotals[currency] = { available: 0, pending: 0, inTransitToBank: 0, instantAvailable: 0 };
        }
        currencyTotals[currency].pending += item.amount || 0;
      });
    }

    // Calculate "In transit to bank" from payouts with status "in_transit" only.
    // Stripe payout amounts are negative (they debit the account), so use Math.abs.
    // Do not infer in-transit status from recently-paid payouts — Stripe's balance
    // API is authoritative; paid payouts have already settled and should not
    // inflate (or deflate) the displayed balance.
    if (rawPayoutsData && Array.isArray(rawPayoutsData)) {
      rawPayoutsData.forEach(payout => {
        if (payout.status === 'in_transit') {
          const currency = payout.currency?.toLowerCase() || 'usd';
          if (!currencyTotals[currency]) {
            currencyTotals[currency] = { available: 0, pending: 0, inTransitToBank: 0, instantAvailable: 0 };
          }
          currencyTotals[currency].inTransitToBank += Math.abs(payout.amount || 0);
        }
      });
    }
    
    // Process instant_available balances from Stripe API
    if (balance.instant_available && Array.isArray(balance.instant_available)) {
      balance.instant_available.forEach(item => {
        const currency = item.currency?.toLowerCase() || 'usd';
        if (!currencyTotals[currency]) {
          currencyTotals[currency] = { available: 0, pending: 0, inTransitToBank: 0, instantAvailable: 0 };
        }
        currencyTotals[currency].instantAvailable += item.amount || 0;
      });
    }
    
    // Find the primary currency (preferring CAD, then USD, then first available)
    let primaryCurrency = 'usd';
    let primaryAmounts = { available: 0, pending: 0, inTransitToBank: 0, instantAvailable: 0 };
    
    if (currencyTotals['cad']) {
      primaryCurrency = 'cad';
      primaryAmounts = currencyTotals['cad'];
    } else if (currencyTotals['usd']) {
      primaryCurrency = 'usd';
      primaryAmounts = currencyTotals['usd'];
    } else {
      // Use first available currency
      const firstCurrency = Object.keys(currencyTotals)[0];
      if (firstCurrency) {
        primaryCurrency = firstCurrency;
        primaryAmounts = currencyTotals[firstCurrency];
      }
    }
    
    const result = {
      available: primaryAmounts.available,
      pending: primaryAmounts.pending,
      inTransitToBank: primaryAmounts.inTransitToBank,
      instantAvailable: primaryAmounts.instantAvailable,
      currency: primaryCurrency
    };
    

    
    return result;
  }, [balance, rawPayoutsData]);

  const loadLifetimeVolume = useCallback(async (clubId: string) => {
    try {
      const balanceTransactions = await apiService.getStripeBalanceTransactions(clubId);
      
      // Calculate transaction types (currently unused)
      // const transactionTypes = balanceTransactions.reduce((acc, transaction) => {
      //   acc[transaction.type] = (acc[transaction.type] || 0) + 1;
      //   return acc;
      // }, {} as Record<string, number>);
      
      // Calculate lifetime volume from charge and payment transactions (actual customer payments)
      // Include both 'charge' and 'payment' to match Stripe's calculation
      const volumeTransactions = balanceTransactions.filter(transaction => {
        return (transaction.type === 'charge' || transaction.type === 'payment') && transaction.amount > 0;
      });
      
      
      // Group transactions by type and show totals (currently unused)
      // const transactionsByType = volumeTransactions.reduce((acc, t) => {
      //   if (!acc[t.type]) {
      //     acc[t.type] = { count: 0, total: 0 };
      //   }
      //   acc[t.type].count++;
      //   acc[t.type].total += t.amount;
      //   return acc;
      // }, {} as Record<string, { count: number; total: number }>);
      
      const totalVolume = volumeTransactions.reduce((sum, transaction) => sum + transaction.amount, 0);
      
      setLifetimeVolume(totalVolume);
    } catch (error) {
      console.error("Error loading lifetime volume:", error);
      setLifetimeVolume(0);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      let club: IClub;
      
      if (clubId) {
        // When clubId is provided (admin dashboard), get club by ID
        club = await apiService.getClub(clubId);
      } else {
        // Fallback to current club (legacy usage)
        club = await apiService.getCurrentClub();
      }
      
      setCurrentClub(club);

      // Determine account type from our own DB record first — this never
      // depends on a live Stripe call succeeding. For Standard accounts, the
      // club manages balances/payouts/refunds directly in their own Stripe
      // Dashboard, so there's no need to (and no reliable way to always)
      // fetch that data through our platform key — skip those calls entirely
      // rather than showing misleading $0.00 placeholders on Stripe errors.
      const [resolvedAccountType] = await Promise.all([
        loadAccount(club._id),
        loadAccountStatus(club._id),
      ]);

      if (resolvedAccountType !== Constants.account_type.standard) {
        await Promise.all([
          loadBalance(club._id),
          loadPayouts(club._id),
          loadPayoutSchedule(club._id),
          loadLifetimeVolume(club._id),
          loadBalanceTransactions(club._id),
          loadRefunds(club._id),
          loadAllActivity(club._id, activityStartDate, activityEndDate)
        ]);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error loading balances and payouts data:", error);
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, loadBalance, loadPayouts, loadPayoutSchedule, loadAccount, loadAccountStatus, loadLifetimeVolume, loadBalanceTransactions, loadRefunds, loadAllActivity]);

  useEffect(() => {
    loadData();
  }, [loadData]);



  const { available, pending, inTransitToBank, currency } = useMemo(() => {
    return getBalanceAmounts();
  }, [getBalanceAmounts]); // Recalculate when balance or payouts data changes
  
  const totalBalance = available + pending + inTransitToBank; // Match Stripe's "Total balance" which includes in transit
  const futurePayouts = available + pending; // Future payouts includes available + pending (excludes in transit)

  // AgGrid column definitions for payouts
  const payoutColDefs: ColDef<IPayoutRow>[] = [
    { 
      field: "amount", 
      headerName: "Amount",
      minWidth: 120, 
      filter: true 
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      filter: true,
      cellRenderer: StatusRenderer,
    },
    { 
      field: "initiated", 
      headerName: "Initiated",
      minWidth: 120, 
      filter: true 
    },
    { 
      field: "arrivalDate", 
      headerName: "Est. Arrival Date",
      minWidth: 150, 
      filter: true 
    }
  ];

  // AgGrid column definitions for refunds
  const refundColDefs: ColDef<IRefundRow>[] = [
    { 
      field: "amount", 
      headerName: "Amount",
      minWidth: 120, 
      filter: true 
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      filter: true,
      cellRenderer: StatusRenderer,
    },
    { 
      field: "created", 
      headerName: "Date Issued",
      minWidth: 120, 
      filter: true 
    },
    { 
      field: "chargeId", 
      headerName: "Charge/Payment",
      minWidth: 150, 
      filter: true,
      cellRenderer: (params: any) => {
        const chargeId = params.value;
        if (!chargeId) return '';
        return chargeId.length > 20 ? `${chargeId.substring(0, 20)}...` : chargeId;
      }
    },
    {
      headerName: "",
      maxWidth: 50,
      minWidth: 50,
      cellRenderer: RefundActionsRenderer,
      pinned: "right",
      cellRendererParams: {
        refreshRefunds: () => loadRefunds(currentClub?._id || ''),
        clubId: currentClub?._id || ''
      },
      resizable: false,
      sortable: false,
      filter: false,
      cellStyle: {
        "overflow": "visible",
        "zIndex": "2"
      }
    }
  ];

  const allActivityColDefs: ColDef[] = [
    {
      headerName: "Date",
      field: "created",
      sortable: true,
      filter: true,
      sort: "desc",
      minWidth: 200,
      valueFormatter: (params) => {
        if (!params.value) return "";
        return new Date(params.value * 1000).toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        });
      },
    },
    {
      headerName: "Type",
      field: "type",
      sortable: true,
      filter: true,
      width: 150,
      cellRenderer: (params: any) => {
        const type = params.value || "";
        const colorMap: Record<string, string> = {
          charge: "#1a8f4e",
          payment: "#1a8f4e",
          payment_intent: "#1a8f4e",
          refund: "#d0392d",
          payment_refund: "#d0392d",
          payout: "#1a6abf",
          payout_cancel: "#888",
          payout_failure: "#d0392d",
          stripe_fee: "#e07b00",
          application_fee: "#e07b00",
          application_fee_refund: "#d0392d",
          adjustment: "#6c3fb5",
          transfer: "#1a6abf",
          transfer_cancel: "#888",
          transfer_failure: "#d0392d",
        };
        const color = colorMap[type] || "#555";
        const label = formatTransactionType(type);
        return (
          <span style={{
            background: `${color}18`,
            color,
            border: `1px solid ${color}44`,
            borderRadius: "12px",
            padding: "2px 10px",
            fontSize: "12px",
            whiteSpace: "nowrap",
          }}>
            {label}
          </span>
        );
      },
    },
    {
      headerName: "Description",
      field: "description",
      sortable: true,
      filter: true,
      flex: 2,
    },
    {
      headerName: "Amount",
      field: "amount",
      sortable: true,
      filter: false,
      width: 120,
      valueFormatter: (params) => {
        if (params.value == null) return "";
        const row = params.data;
        return formatCurrency(params.value, row.currency);
      },
    },
    {
      headerName: "Fee",
      field: "fee",
      sortable: true,
      filter: false,
      width: 100,
      valueFormatter: (params) => {
        if (params.value == null) return "";
        const row = params.data;
        return formatCurrency(params.value, row.currency);
      },
    },
    {
      headerName: "Net",
      field: "net",
      sortable: true,
      filter: false,
      width: 120,
      valueFormatter: (params) => {
        if (params.value == null) return "";
        const row = params.data;
        return formatCurrency(params.value, row.currency);
      },
      cellStyle: (params) => ({
        color: params.value >= 0 ? "#1a8f4e" : "#d0392d",
        fontWeight: 600,
      }),
    },
    {
      headerName: "Status",
      field: "status",
      sortable: true,
      filter: true,
      width: 120,
      cellRenderer: StatusRenderer,
    },
  ];

  const defaultColDef: ColDef = {
    flex: 1
  };

  if (loading) {
    return (
      <div className={`BalancesPayouts ${hideHeader ? 'admin-embedded' : ''}`}>
        {!hideHeader && (
          <Helmet>
            <title>Balances & Payouts - Eager Dog</title>
          </Helmet>
        )}
        <div className="ManagePage">
          <div className="accountInner">
            {!hideHeader && (
              <div className="pageHeader">
                <h1>Balances & Payouts</h1>
              </div>
            )}
            
            {/* Skeleton for metrics */}
            <div className="metrics">
              <div className="metricItem skeleton">
                <div className="skeleton-line large"></div>
                <div className="skeleton-line small"></div>
              </div>
              <div className="metricItem skeleton">
                <div className="skeleton-line large"></div>
                <div className="skeleton-line small"></div>
              </div>
              <div className="metricItem skeleton">
                <div className="skeleton-line large"></div>
                <div className="skeleton-line small"></div>
              </div>
            </div>

            {/* Skeleton for account info */}
            <div className="account-info-section">
              <div className="account-info-card skeleton">
                <div className="skeleton-line medium"></div>
                <div className="skeleton-line small"></div>
                <div className="skeleton-line small"></div>
              </div>
            </div>

            {/* Skeleton for payouts table */}
            <div className="payouts-section">
              <div className="table-header">
                <div className="skeleton-line medium"></div>
              </div>
              <div className="tableWrap skeleton">
                <div className="skeleton-table">
                  <div className="skeleton-row header">
                    <div className="skeleton-cell"></div>
                    <div className="skeleton-cell"></div>
                    <div className="skeleton-cell"></div>
                    <div className="skeleton-cell"></div>
                  </div>
                  <div className="skeleton-row">
                    <div className="skeleton-cell"></div>
                    <div className="skeleton-cell"></div>
                    <div className="skeleton-cell"></div>
                    <div className="skeleton-cell"></div>
                  </div>
                  <div className="skeleton-row">
                    <div className="skeleton-cell"></div>
                    <div className="skeleton-cell"></div>
                    <div className="skeleton-cell"></div>
                    <div className="skeleton-cell"></div>
                  </div>
                  <div className="skeleton-row">
                    <div className="skeleton-cell"></div>
                    <div className="skeleton-cell"></div>
                    <div className="skeleton-cell"></div>
                    <div className="skeleton-cell"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Balances here are club-wide Stripe account totals, not per-event. Clubs
  // regularly compare these with a single event's revenue and open tickets
  // when they don't match, so say it up front — between the page title and
  // the KPI cards, outside the grey metrics panel.
  const scopeNote =
    accountType !== Constants.account_type.standard ? (
      <div className="balance-scope-note">
        <strong>These balances are for your whole club, not a single event.</strong>{" "}
        They come from your Stripe account and cover every event, minus payouts already sent to your bank.
        Offline payments (cheque, cash) never appear here, and payments still clearing show under Available Soon.
        To reconcile one event, use the Revenue figures and Transactions export on that event's Manage Event page.
      </div>
    ) : null;

  return (
    <div className={`BalancesPayouts ${hideHeader ? 'admin-embedded' : ''}`}>
      <Helmet>
        <title>Balances & Payouts - Eager Dog</title>
        <meta name="description" content="Manage your club's balance and payout information" />
      </Helmet>

      {/* Embedded (Manage Kennel Club): the parent renders the title/subtitle,
          so the note goes above the grey panel that holds the KPI cards. */}
      {hideHeader && scopeNote}
      
      <div className="ManagePage">
        <div className="accountInner">
          {!hideHeader && (
            <>
              <div className="breadcrumb">
                <div className="path">
                  <span>Account Settings</span>
                </div>
                <div className="path">
                  <span>Balances & Payouts</span>
                </div>
              </div>

              <h1>Balances & Payouts</h1>
              {scopeNote}
            </>
          )}

          {/* Balance Metrics */}
          {accountType === Constants.account_type.standard ? (
            <div className="metrics standard-account-metrics">
              <div className="metricItem" style={{ flex: 1 }}>
                <div className="label">Balances, payouts, and payout schedule are managed directly in your connected Stripe account.</div>
                <div className="subtext">
                  <button
                    className="schedule-link"
                    onClick={() => window.open('https://dashboard.stripe.com/', '_blank')}
                  >
                    Open Stripe Dashboard →
                  </button>
                </div>
              </div>
            </div>
          ) : payoutSchedule?.interval === 'manual' ? (
            <div className="metrics">
              <div className="metricItem">
                <div className="num">{formatCurrency(totalBalance, currency)}</div>
                <div className="label">Current Balance (all events)</div>
                <div className="subtext">Lifetime Volume, all events, before fees and refunds: {formatCurrency(lifetimeVolume, currency)}</div>
              </div>
              <div className="metricItem">
                <div className="num">{formatCurrency(pending, currency)}</div>
                <div className="label">Available Soon</div>
                <div className="subtext">
                  <button 
                    className="schedule-link" 
                    onClick={() => openModal("viewSchedule")}
                  >
                    View payout schedule
                  </button>
                </div>
              </div>
              <div className="metricItem">
                <div className="num">{formatCurrency(available, currency)}</div>
                <div className="label">Available for Payout</div>
              </div>
              <div className="metricItem">
                <div className="num">{formatCurrency(inTransitToBank, currency)}</div>
                <div className="label">In Transit to Bank</div>
              </div>
              
            </div>
          ) : (
            <div className="metrics">
              <div className="metricItem">
                <div className="num">{formatCurrency(totalBalance, currency)}</div>
                <div className="label">Total Balance (all events)</div>
              </div>
              <div className="metricItem">
                <div className="num">{formatCurrency(futurePayouts, currency)}</div>
                <div className="label">Future Payouts</div>
                <div className="subtext">
                  <button 
                    className="schedule-link" 
                    onClick={() => openModal("viewSchedule")}
                  >
                    View payout schedule
                  </button>
                </div>
              </div>
              <div className="metricItem">
                <div className="num">{formatCurrency(inTransitToBank, currency)}</div>
                <div className="label">In Transit to Bank</div>
              </div>
              <div className="metricItem">
                <div className="num">{formatCurrency(lifetimeVolume, currency)}</div>
                <div className="label">Lifetime Total Volume (all events, before fees and refunds)</div>
              </div>
            </div>
          )}

          {/* Payout Details */}
          <div className="payout-details">
            <div className="payout-details-content">
              <div className="payout-details-header">
                <div className="payout-details-title">
                  <h2>
                    Stripe Details
                    {accountType === Constants.account_type.standard && (
                      <span style={{
                        marginLeft: "10px",
                        fontSize: "12px",
                        fontWeight: 600,
                        background: "#e8f0fe",
                        color: "#1a6abf",
                        border: "1px solid #1a6abf44",
                        borderRadius: "12px",
                        padding: "2px 10px",
                        verticalAlign: "middle",
                      }}>
                        Standard Account
                      </span>
                    )}
                  </h2>
                  <p>
                    {accountType === Constants.account_type.standard
                      ? "Your payout schedule and bank account are managed directly in your Stripe Dashboard."
                      : "Manage your payout schedule and bank account information."}
                  </p>
                </div>
                
                <div className="payout-details-actions">
                  {accountType === Constants.account_type.standard ? (
                    <div className="primary-actions">
                      <Button
                        onClick={() => window.open(`https://dashboard.stripe.com/`, "_blank")}
                      >
                        Open Stripe Dashboard
                      </Button>
                      <Button
                        className="secondary"
                        onClick={() => { setModalContent("disconnectStandard"); toggle(); }}
                      >
                        Disconnect
                      </Button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "row", gap: "12px", alignItems: "flex-start" }}>
                      <Button onClick={updatePayoutSchedule}>
                        Payout Schedule
                      </Button>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
                        <Button onClick={updateBankAccount}>
                          Edit Stripe Account
                        </Button>
                        {/* Once a club has an account configured (Custom or
                            Standard), switching the connected Stripe account
                            is disruptive (it deletes/replaces the existing
                            account record) — only offer this before any
                            account has been set up. */}
                        {!accountConfigured && (
                          <button
                            onClick={() => { setModalContent("connectStandard"); toggle(); }}
                            style={{
                              background: "none",
                              border: "none",
                              padding: "0",
                              cursor: "pointer",
                              color: "#F25A2D",
                              fontSize: "14px",
                              fontWeight: 500,
                              textDecoration: "underline",
                            }}
                          >
                            Use an Existing Stripe Account
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {accountType !== Constants.account_type.standard && payoutSchedule?.interval === 'manual' && (
                    <div className="secondary-actions">
                      <Button onClick={() => openModal("requestPayout")}>
                        Request Payout
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="payout-details-info">
                {/* Account Status Section */}
                <div className="account-status-section">
                  <div className="account-status-header">
                    <span className="account-status-label">Account Status</span>
                  </div>
                  <div className={`account-status-content ${getAccountStatusMessage().status}`}>
                    <div className="account-status-icon">
                      {getAccountStatusMessage().status === "requirements_met" && <span className="status-icon success">✓</span>}
                      {(getAccountStatusMessage().status === "past_due" || getAccountStatusMessage().status === "currently_due") && <span className="status-icon warning">!</span>}
                      {(getAccountStatusMessage().status === "eventually_due" || getAccountStatusMessage().status === "info") && <span className="status-icon info">i</span>}
                      {getAccountStatusMessage().status === "loading" && <span className="status-icon loading">•</span>}
                      {getAccountStatusMessage().status === "unknown" && <span className="status-icon warning">?</span>}
                    </div>
                    <div className="account-status-message">
                      {getAccountStatusMessage().message}
                    </div>
                    {getAccountStatusMessage().actionNeeded && (
                      <div className="account-status-action">
                        <Button onClick={handleAccountAction}>
                          Update Account
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="payout-info-grid">
                  <div className="payout-info-item">
                    <span className="payout-info-label">Account Name</span>
                    <span className="payout-info-value">{getAccountName()}</span>
                  </div>
                  <div className="payout-info-item">
                    <span className="payout-info-label">Payout Schedule</span>
                    <span className="payout-info-value">
                      {getCurrentScheduleText()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabbed Tables Section */}
          {accountType === Constants.account_type.standard ? (
            <div className="standard-account-tables-note">
              <p>
                Payouts, refunds, and transaction activity for your connected
                Stripe account are available directly in your{" "}
                <button
                  className="schedule-link"
                  onClick={() => window.open('https://dashboard.stripe.com/', '_blank')}
                  style={{ display: "inline" }}
                >
                  Stripe Dashboard
                </button>.
              </p>
            </div>
          ) : (
          <div className="BalancesPayouts">
            <TabContent setTab={setTab} activeTab={activeTab} tabs={tabs}>
            <div className="tableWrap">
              <AgGridReact
                rowData={rowData}
                suppressRowTransform={true}
                columnDefs={payoutColDefs}
                defaultColDef={defaultColDef}
                theme={theme}
                pagination={true}
                paginationPageSize={20}
                onRowClicked={handlePayoutRowClick}
                getRowId={(params) => params.data.id}
              />
            </div>
            <div>
              {refundsLoading ? (
                <Loading message="Loading refunds..." />
              ) : (
                <div className="tableWrap">
                  <AgGridReact
                    rowData={refundsData}
                    suppressRowTransform={true}
                    columnDefs={refundColDefs}
                    defaultColDef={defaultColDef}
                    theme={theme}
                    pagination={true}
                    paginationPageSize={20}
                  />
                </div>
              )}
            </div>
            <div className="allActivityTab">
              <div className="allActivityFilters">
                <Input
                  label="From"
                  id="activityStartDate"
                  type="date"
                  value={activityStartDate}
                  onChange={(e) => setActivityStartDate(e.target.value)}
                />
                <Input
                  label="To"
                  id="activityEndDate"
                  type="date"
                  value={activityEndDate}
                  onChange={(e) => setActivityEndDate(e.target.value)}
                />
                <Button
                  onClick={() => currentClub && loadAllActivity(currentClub._id, activityStartDate, activityEndDate)}
                >
                  Apply
                </Button>
              </div>
              {allActivityLoading ? (
                <Loading message="Loading activity..." />
              ) : (
                <div className="tableWrap">
                  <AgGridReact
                    rowData={allActivityData}
                    suppressRowTransform={true}
                    columnDefs={allActivityColDefs}
                    defaultColDef={defaultColDef}
                    theme={theme}
                    pagination={true}
                    paginationPageSize={50}
                    getRowId={(params) => params.data.id}
                  />
                </div>
              )}
            </div>
          </TabContent>
          </div>
          )}
        </div>
      </div>

      <Modal
        className={
          modalContent === "updateBank" ? "updateBankModal" :
          modalContent === "connectStandard" ? "updateBankModal" :
          modalContent === "disconnectStandard" ? "updateBankModal" :
          modalContent === "updateSchedule" ? "updateScheduleModal" :
          modalContent === "requestPayout" ? "requestPayoutModal" :
          modalContent === "viewSchedule" ? "viewScheduleModal" :
          modalContent === "payoutDetails" ? "payoutDetailsModal" :
          ""
        }
        isShown={isShown}
        hide={toggle}
        modalContent={getModalContent() || <div />}
      />
    </div>
  );
};

export default BalancesPayouts; 